import { useCallback, useState } from 'react';

type DotNotationKeys<
  T,
  Prefix extends string = '',
  Depth extends number[] = [],
> = Depth['length'] extends 5
  ? never
  : {
      [K in keyof T & string]: T[K] extends object
        ? `${Prefix}${K}` | DotNotationKeys<T[K], `${Prefix}${K}.`, [...Depth, 1]>
        : `${Prefix}${K}`;
    }[keyof T & string];

type ValueType<T, K extends string> = K extends `${infer Key}.${infer Rest}`
  ? Key extends keyof T
    ? ValueType<T[Key], Rest>
    : never
  : K extends keyof T
    ? T[K]
    : never;

export type UpdateDataFunction<T> = {
  (field: Partial<T>): void;
  <K extends DotNotationKeys<T>>(field: K, value: ValueType<T, K>): void;
};

function deepMerge<T>(target: T, source: Partial<T>): T {
  if (typeof source !== 'object' || source === null) {
    return source as T;
  }

  const result = { ...target };

  for (const key in source) {
    if (source[key] !== undefined) {
      if (typeof source[key] === 'object' && typeof result[key] === 'object') {
        result[key] = deepMerge(result[key], source[key] as Partial<T[typeof key]>);
      } else {
        result[key] = source[key] as T[typeof key];
      }
    }
  }

  return result;
}

export default function useData<T extends Record<string, any>>(initial: T) {
  const [data, setData] = useState<T>(initial);

  const updateData = useCallback<UpdateDataFunction<T>>(
    (fieldOrFields: object | string, value?: any) => {
      if (typeof fieldOrFields === 'object') {
        return setData((current) => deepMerge(current, fieldOrFields));
      }

      const field = fieldOrFields as DotNotationKeys<T>;

      setData((current) => {
        const newData = { ...current };
        const parts = field.split('.');
        let obj: any = newData;

        while (parts.length > 1) {
          const key = parts.shift()!;
          obj = obj[key] = { ...obj[key] };
        }

        obj[parts[0]] = value;

        return newData;
      });
    },
    [],
  );

  const resetData = () => setData({ ...initial });

  return {
    data,
    setData,
    updateData,
    resetData,
  };
}
